<template>
  <div v-if="model" class="col-lg-12 col-md-12 col-xl-10 col-xxl-7">
    <div v-if="model.IdEstado" class="empresa-estado-section mb-3">
      <div class="d-flex align-items-center flex-wrap gap-2">
        <span class="dx-field-item-label-text">{{ $t("mis_datos.mi_empresa.estado") }}</span>
        <span :class="['status-badge', 'estado-empresa-agente', model.IdEstado]">
          {{ $t(`empresa_agente.estado.${model.IdEstado}`) }}
        </span>
        <span class="info-icon d-inline text-primary">
          <i class="fas fa-info-circle" :id="estadoEmpresaTooltipId"></i>
          <DxTooltip :hide-on-outside-click="false" :target="`#${estadoEmpresaTooltipId}`" show-event="mouseenter"
            hide-event="mouseleave">
            <template #content>
              <div>{{ $t(`empresa_agente_estado_tooltip.${model.IdEstado}`) }}</div>
            </template>
          </DxTooltip>
        </span>
      </div>
      <div v-if="esEmpresaRechazada && model.MotivoRechazo" class="mt-2">
        <span class="dx-field-item-label-text d-block">{{ $t("mis_datos.mi_empresa.motivo_rechazo") }}</span>
        <span class="mt-1 d-block">{{ model.MotivoRechazo }}</span>
      </div>
    </div>
    <DxForm id="formularioEmpresaColaboradora" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" validation-group="validacionEmpresaColaboradora">

      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <!-- Datos de la empresa -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="RazonSocial" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_colaboradoras.form.razon_social')" />
          <DxRequiredRule :message="$t('empresas_colaboradoras.form.validacion.razon_social')" />
          <DxCustomRule
            :validation-callback="validarRazonSocial"
            :message="$t('empresas_colaboradoras.form.validacion.campo_no_modificable_tras_validacion')"
            :reevaluate="true"
          />
        </DxSimpleItem>
        <DxSimpleItem data-field="Documento" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_colaboradoras.form.cif')" />
          <DxRequiredRule :message="$t('empresas_colaboradoras.form.validacion.cif')" />
          <DxPatternRule :pattern="cifPattern" :message="$t('empresas_colaboradoras.form.validacion.cif_patron')" />
          <DxCustomRule
            :validation-callback="validarDocumento"
            :message="$t('empresas_colaboradoras.form.validacion.campo_no_modificable_tras_validacion')"
            :reevaluate="true"
          />
        </DxSimpleItem>
        <DxSimpleItem data-field="Web" editor-type="dxTextBox" :col-span="6">
          <DxLabel :text="$t('empresas_colaboradoras.form.sitio_web')" />
          <DxRequiredRule :message="$t('empresas_colaboradoras.form.validacion.sitio_web')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Telefono" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_colaboradoras.form.telefono')" />
          <DxRequiredRule :message="$t('empresas_colaboradoras.form.validacion.telefono')" />
          <DxPatternRule :pattern="telefonoPattern"
            :message="$t('empresas_colaboradoras.form.validacion.telefono_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Email" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_colaboradoras.form.email')" />
          <DxRequiredRule :message="$t('empresas_colaboradoras.form.validacion.email')" />
          <DxEmailRule :message="$t('empresas_colaboradoras.form.validacion.email_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="FechaCreacion" editor-type="dxDateBox" :col-span="2">
          <DxLabel :text="$t('empresas_colaboradoras.form.fecha_creacion')" />
          <DxRequiredRule :message="$t('empresas_colaboradora.form.validacion.fecha_creacion')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="FormaJuridica" editor-type="dxSelectBox" :col-span="2"
          :editor-options="selectFormaJuridicaOptions">
          <DxLabel :text="$t('mis_datos.datos_fiscales.forma_juridica')" />
          <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.forma_juridica')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="DenoFactura" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('mis_datos.datos_fiscales.deno_factura')" />
          <DxPatternRule :pattern="getDenoFacturaPattern()"
            :message="$t('mis_datos.datos_fiscales.validacion.deno_factura_patron')" />
          <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.deno_factura')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Iban" editor-type="dxTextBox" :col-span="3" :editor-options="ibanOptions">
          <DxLabel :text="$t('mis_datos.datos_fiscales.iban')" />
          <DxPatternRule :pattern="ibanPattern" :message="$t('mis_datos.datos_fiscales.validacion.iban_patron')" />
          <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.iban')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="3">
        </DxEmptyItem>
      </DxGroupItem>

      <!-- Datos fiscales -->
      <DxGroupItem :col-count="6" :col-span="6">

        <!-- Aplica IRPF -->
        <DxGroupItem :col-span="2" :visible="!soySociedad">
          <DxSimpleItem>
            <DxRadioGroup v-model="aplicarIrpf" :items="radioYesNoValues" value-expr="valor" display-expr="texto"
              layout="horizontal" validation-message-mode="always">
            </DxRadioGroup>
            <DxLabel :text="$t('mis_datos.datos_fiscales.aplica_irpf')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="PorcentajeIrpf" editor-type="dxNumberBox" :editor-options="irpfNumberOptions"
            :visible="aplicarIrpf">
            <DxLabel :text="$t('mis_datos.datos_fiscales.irpf')" />
            <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
              :message="$t('mis_datos.datos_fiscales.validacion.irpf_mayor_0')" />
            <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.irpf')" />
          </DxSimpleItem>
        </DxGroupItem>

        <!-- Con autofactura -->
        <DxGroupItem :col-span="2">
          <DxSimpleItem>
            <DxRadioGroup v-model="conAutofactura" :items="radioYesNoValues" value-expr="valor" display-expr="texto"
              layout="horizontal" validation-message-mode="always">
            </DxRadioGroup>
            <DxLabel :text="$t('mis_datos.datos_fiscales.con_autofacturacion')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="DiasAutofacturacion" editor-type="dxTagBox" :editor-options="diasAutofacturacionOptions"
            :visible="conAutofactura" :col-span="2">
            <DxLabel :text="$t('mis_datos.datos_fiscales.dias_autofacturacion')" />
          </DxSimpleItem>
        </DxGroupItem>

        <DxEmptyItem :col-span="2" ></DxEmptyItem>

        <DxEmptyItem :col-span="2" :visible="soySociedad"></DxEmptyItem>
      </DxGroupItem>

      <!-- Dirección física -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :col-span="6">
          <span class="dx-field-item-label-text text-success">
            {{ $t("empresas_inmobiliarias.form.direccion_fisica") }}
          </span>
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.CodigoPostal" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('empresas_colaboradoras.form.codigo_postal')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.codigo_postal')" />
          <DxPatternRule :pattern="codigoPostalPattern"
            :message="$t('empresa_colaboradora.form.validacion.codigo_postal_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Provincia" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('empresas_colaboradoras.form.provincia')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.provincia')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Municipio" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_colaboradoras.form.municipio')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.municipio')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.TipoVia" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('empresas_colaboradoras.form.tipo_de_via')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.tipo_de_via')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.NombreVia" editor-type="dxTextBox" :col-span="4">
          <DxLabel :text="$t('empresas_colaboradoras.form.nombre_via')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.nombre_via')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Numero" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('empresas_colaboradoras.form.numero')" />
          <DxRequiredRule :message="$t('empresa_colaboradora.form.validacion.numero')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Escalera" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('empresas_colaboradoras.form.escalera')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Piso" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('empresas_colaboradoras.form.piso')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Puerta" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('empresas_colaboradoras.form.puerta')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="2">
        </DxEmptyItem>
      </DxGroupItem>

      <!-- Logo -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :col-span="3">
          <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderValueChanged">
          </DxFileUploader>
          <DxLabel :text="$t('mis_datos.datos_fiscales.logo')" />
        </DxSimpleItem>
        <DxGroupItem :col-span="3">
          <DxSimpleItem :visible="model?.ImagenB64 != undefined && model?.ImagenB64 != ''">
            <div class="d-flex justify-content-center">
              <img :src="model.ImagenB64 ?? ''" alt="Imagen de la empresa de agentes" style="max-width: 100%; margin-top: 10px;" />
            </div>
          </DxSimpleItem>
        </DxGroupItem>
      </DxGroupItem>

        <!-- Logo portal-->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :col-span="3">
          <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderLogoPortalValueChanged">
          </DxFileUploader>
          <DxLabel :text="$t('mis_datos.datos_fiscales.logoPortal')" />
        </DxSimpleItem>
        <DxGroupItem :col-span="3">
          <DxSimpleItem :visible="model?.LogoPortalB64 != undefined && model?.LogoPortalB64 != ''">
            <div class="d-flex justify-content-center">
              <img :src="model.LogoPortalB64 ?? ''" alt="Imagen de la empresa de agentes" style="max-width: 60px; max-height: 60px; margin-top: 10px;" />
            </div>
          </DxSimpleItem>
        </DxGroupItem>
      </DxGroupItem>

      <!-- Botón de guardar -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxEmptyItem :col-span="2"></DxEmptyItem>
        <DxButtonItem :col-span="4" :button-options="optSaveButton"></DxButtonItem>
      </DxGroupItem>

      <template #whitelistLabel="{ data }">
        <div id="template-content">
          <span class="dx-field-item-label-text">{{
            $t('empresas_colaboradoras.form.con_whitelist_servicios') }}</span>
          <div class="info-icon d-inline text-primary ms-1">
            <i class="fas fa-info-circle" id="whitelistLabel"></i>
            <DxTooltip :hide-on-outside-click="false" target="#whitelistLabel" show-event="mouseenter"
              hide-event="mouseleave">
              <template #content>
                <div id="tooltip-content">{{
                  $t('empresas_colaboradoras.form.texto_informativo_whitelist_servicios') }}</div>
              </template>
            </DxTooltip>
          </div>
        </div>
      </template>
    </DxForm>

  </div>
</template>
<script setup lang="ts">
import { EstadoEmpresaAgentesEnum, FormaJuridicaEnum, type DetailEmpresaColaboradoraModel, type EditEmpresaColaboradoraModel } from "@/.shared/.api";
import { useApi } from "@/.shared/.api/integration";
import { saveButtonProps } from "@/.shared/helpers/button-helper";
import { getColumnsLookupAsNumber, getLookup } from "@/.shared/helpers/grid-helper";
import { useIntl } from "@/.shared/services/i18n";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { useLogoStore } from '@/composables/useLogoStore';
import { DxFileUploader, DxTagBox } from 'devextreme-vue';
import { DxTooltip } from 'devextreme-vue/tooltip';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxEmptyItem,
  DxButtonItem,
  DxPatternRule,
  DxCompareRule,
  DxRequiredRule,
  DxGroupItem,
  DxEmailRule,
  DxCustomRule,
  DxSimpleItem
} from "devextreme-vue/form";
import { DxRadioGroup } from "devextreme-vue";
import validationEngine from "devextreme/ui/validation_engine";
import { getDenoFacturaPattern } from "@helpers/text-box-helper";
import { useIbanField } from "@composables/devextreme";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {
  recargarDatosEmpresaMisDatos,
  registrarRecargaDatosEmpresaMisDatos,
} from "@/composables/misDatosRecargaEmpresa";
import { ApiError } from "@/.shared/services/http";
import { COMPANY_WITH_DUPLICATED_CIF, COMPANY_WITH_DUPLICATED_DENO_FACTURA } from "@/.shared/errors/codes";
import {
  crearValidadorCampoInmutable,
  useEmpresaAgentesCamposIdentidadBloqueados,
} from "@/composables/empresaAgentesCamposIdentidadBloqueados";

const model = ref<DetailEmpresaColaboradoraModel>({} as DetailEmpresaColaboradoraModel);
const datosOriginales = ref<{ RazonSocial?: string; Documento?: string }>();
const { camposIdentidadBloqueados } = useEmpresaAgentesCamposIdentidadBloqueados();
const validarRazonSocial = crearValidadorCampoInmutable(() => datosOriginales.value?.RazonSocial, camposIdentidadBloqueados);
const validarDocumento = crearValidadorCampoInmutable(() => datosOriginales.value?.Documento, camposIdentidadBloqueados);
const formInstance = ref<DxForm>();
const estadoEmpresaTooltipId = "estadoEmpresaColaboradoraInfo";
const api = useApi();
const intl = useIntl();
const { refreshLogo } = useLogoStore();
const conAutofactura = ref<boolean>(false);
const aplicarIrpf = ref<boolean>(false);

const telefonoPattern = ref<RegExp>(/^\d{9}$/);
const codigoPostalPattern = ref<RegExp>(/^\d{5}$/);
const cifPattern = ref<RegExp>(/^[A-Za-z]\d{7}[A-Za-z0-9]$|^\d{8}[A-Za-z]$/);

const { ibanPattern, getIbanFieldOptions } = useIbanField();
const ibanOptions = getIbanFieldOptions();

let desregistrarRecargaEmpresa: (() => void) | undefined;

onMounted(async () => {
  await cargarDatos();
  desregistrarRecargaEmpresa = registrarRecargaDatosEmpresaMisDatos(() => cargarDatos({ showLoading: false }));
});

onUnmounted(() => {
  desregistrarRecargaEmpresa?.();
});

const cargarDatos = async (options?: { showLoading?: boolean }) => {
  await api.callStandardApi("getEmpresaColaboradoraByUserId", { showLoading: options?.showLoading ?? true })
    .then(data => {
      model.value = data;
      datosOriginales.value = {
        RazonSocial: data.RazonSocial,
        Documento: data.Documento,
      };
      conAutofactura.value = (model.value as DetailEmpresaColaboradoraModel).DiasAutofacturacion.length > 0;
      aplicarIrpf.value = (model.value as DetailEmpresaColaboradoraModel).PorcentajeIrpf != null &&
        (model.value as DetailEmpresaColaboradoraModel).PorcentajeIrpf != 0
    })
    .catch();
}

watch(() => conAutofactura.value, (newVal) => {
  if (!newVal)
    model.value.DiasAutofacturacion = [];
})

watch(() => aplicarIrpf.value, (newVal) => {
  if (!newVal)
    model.value.PorcentajeIrpf = 0;
})

const radioYesNoValues = [
  { valor: true, texto: intl.i18n.t("shared.ui.literals.yes") },
  { valor: false, texto: intl.i18n.t("shared.ui.literals.no") },
];

const optSaveButton = {
  ...saveButtonProps(),
  onClick: function () {
    const validationResult = formInstance.value?.instance?.validate();
    if (validationResult?.isValid) {
      save();
    }
  }
};

const save = async () => {
  var res = validationEngine.validateGroup("validacionEmpresaColaboradora");
  if (res.isValid) {
    try {
      await api.callStandardApi("editEmpresaColaboradora", { showLoading: true }, model.value as EditEmpresaColaboradoraModel)
        .then(async () => {
          await recargarDatosEmpresaMisDatos();
          toastSuccess(intl.i18n.t("empresa_colaboradora.edicion_exito"));
          refreshLogo();
        });
    } catch (e: any) {
      if (e instanceof ApiError && e.details?.errorList.length === 1 && [COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF].includes(e.details.errorList[0].Code)) {
        toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
      }
      else throw e;
      return false;
    }
    return true;
  } else {
    toastError(intl.i18n.t('empresa_colaboradora.revise_validacion'));
  }
};

const selectFormaJuridicaOptions = {
  dataSource: getLookup('mis_datos', 'forma_juridica').dataSource,
  valueExpr: 'value',
  displayExpr: 'text'
};

const irpfNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal');
    }
  },
  min: 0,
  max: 100
};

const diasAutofacturacionOptions = {
  dataSource: getColumnsLookupAsNumber('mis_datos', 'dias_autofacturacion_valores').dataSource,
  valueExpr: 'value',
  displayExpr: 'text'
}

function onFileUploaderValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (model.value) {
        model.value.ImagenB64 = base64;
      }
    };
    reader.readAsDataURL(file);
  }
}

function onFileUploaderLogoPortalValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (model.value) {
        model.value.LogoPortalB64 = base64;
      }
    };
    reader.readAsDataURL(file);
  }
}

const soySociedad = computed(() => {
  return model.value?.FormaJuridica == FormaJuridicaEnum.SociedadLimitada;
});

const esEmpresaRechazada = computed(() => model.value?.IdEstado === EstadoEmpresaAgentesEnum.Rechazada);
</script>

<style lang="scss" scoped>
.status-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 5px;
  text-align: center;
}
</style>